import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const ExportQueueIndicator = ({ 
  queueCount = 0,
  completedCount = 0,
  processingExport = null,
  onDownloadClick = null,
  onClearCompleted = null,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasActivity = queueCount > 0 || completedCount > 0 || processingExport;

  if (!hasActivity) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getStatusIcon = () => {
    if (processingExport) return 'Loader2';
    if (completedCount > 0) return 'CheckCircle';
    return 'Clock';
  };

  const getStatusColor = () => {
    if (processingExport) return 'text-warning';
    if (completedCount > 0) return 'text-success';
    return 'text-muted-foreground';
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Indicator Button */}
      <button
        onClick={toggleExpanded}
        className={`
          inline-flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium
          bg-card border border-border hover:bg-muted
          transition-all duration-150 ease-smooth
          ${completedCount > 0 ? 'ring-1 ring-success ring-opacity-20' : ''}
        `}
        title="Export queue status"
      >
        <Icon 
          name={getStatusIcon()} 
          size={12} 
          className={`${getStatusColor()} ${processingExport ? 'animate-spin' : ''}`}
        />
        
        {processingExport && (
          <span className="text-warning">Processing</span>
        )}
        
        {queueCount > 0 && (
          <span className="text-muted-foreground">
            {queueCount} queued
          </span>
        )}
        
        {completedCount > 0 && (
          <span className="text-success font-semibold">
            {completedCount} ready
          </span>
        )}
        
        <Icon 
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
          size={10} 
          className="text-muted-foreground"
        />
      </button>
      {/* Expanded Queue Details */}
      {isExpanded && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-popover border border-border rounded-md shadow-modal z-50 animate-fade-in">
          <div className="p-3">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground">Export Queue</h4>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleExpanded}
                iconName="X"
                className="h-6 w-6"
              />
            </div>

            {/* Processing Export */}
            {processingExport && (
              <div className="mb-3 p-2 bg-warning bg-opacity-10 border border-warning border-opacity-20 rounded-md">
                <div className="flex items-center space-x-2">
                  <Icon name="Loader2" size={14} className="text-warning animate-spin" />
                  <span className="text-sm text-foreground font-medium">
                    {processingExport?.name}
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Processing... {processingExport?.progress || 0}%
                </div>
                {processingExport?.progress > 0 && (
                  <div className="mt-2 w-full h-1 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-warning transition-all duration-300"
                      style={{ width: `${processingExport?.progress}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Queued Exports */}
            {queueCount > 0 && (
              <div className="mb-3">
                <div className="text-xs text-muted-foreground mb-2">
                  {queueCount} export{queueCount !== 1 ? 's' : ''} in queue
                </div>
                <div className="space-y-1">
                  {Array.from({ length: Math.min(queueCount, 3) })?.map((_, index) => (
                    <div key={index} className="flex items-center space-x-2 text-xs">
                      <Icon name="Clock" size={12} className="text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Export {index + 1} - Waiting
                      </span>
                    </div>
                  ))}
                  {queueCount > 3 && (
                    <div className="text-xs text-muted-foreground pl-5">
                      +{queueCount - 3} more...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Completed Exports */}
            {completedCount > 0 && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-muted-foreground">
                    {completedCount} export{completedCount !== 1 ? 's' : ''} ready
                  </div>
                  {onClearCompleted && (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={onClearCompleted}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  {Array.from({ length: Math.min(completedCount, 3) })?.map((_, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-success bg-opacity-10 border border-success border-opacity-20 rounded">
                      <div className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={12} className="text-success" />
                        <span className="text-xs text-foreground">
                          Report_{index + 1}.xlsx
                        </span>
                      </div>
                      {onDownloadClick && (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => onDownloadClick(index)}
                          iconName="Download"
                          className="h-6 w-6 text-success hover:bg-success hover:text-success-foreground"
                        />
                      )}
                    </div>
                  ))}
                  {completedCount > 3 && (
                    <div className="text-xs text-muted-foreground pl-5">
                      +{completedCount - 3} more downloads available
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/export-and-reports-center'}
                iconName="ExternalLink"
                iconPosition="right"
                fullWidth
                className="text-xs"
              >
                View Export Center
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Overlay */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default ExportQueueIndicator;